import { Trans } from 'react-i18next';
import {
  CustomizationDataDto,
  ExcelExportRequestDto,
  SortColumnDto,
  TableauShareActionsConfigDto,
  TableShareActionsConfigDto,
  VegaChartShareActionsConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import { portalFilenamify, WidgetMoreAction } from '@asset-metrix/portal-ui';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';

import { datatableExcelDownload } from '../../api/portalGateway/datatableExcelExport';
import { DOWNLOAD_DATE_PREFIX_FORMAT } from '../../config';
import { actionWithAsyncNotification } from '../../utils/actionWithAsyncNotification';

// Group data downloads apply normal filtering (WHERE-clause), so they belong
// right after the filtered download actions — not after unfiltered ones.
const FILTERED_DOWNLOAD_ACTION_KEYS = new Set([
  'downloadXlsx',
  'downloadCsv',
  'downloadXlsxReadOnly',
]);

export function buildShareActionsWithInsertIndex<
  T extends { actionKey: string },
>(
  shareActions: T[],
  buildAction: (sa: T) => WidgetMoreAction[]
): { actions: WidgetMoreAction[]; insertIndex: number } {
  const actions: WidgetMoreAction[] = [];
  let insertIndex = 0;

  for (const sa of shareActions) {
    actions.push(...buildAction(sa));
    if (FILTERED_DOWNLOAD_ACTION_KEYS.has(sa.actionKey)) {
      insertIndex = actions.length;
    }
  }

  return { actions, insertIndex };
}

function exportNotifications(fileName: string) {
  return {
    startMessage: (
      <Trans i18nKey="app.notifications.export.loading" values={{ fileName }} />
    ),
    successMessage: (
      <Trans i18nKey="app.notifications.export.success" values={{ fileName }} />
    ),
    errorMessage: (
      <Trans i18nKey="app.notifications.export.error" values={{ fileName }} />
    ),
    showBackLink: true,
  };
}

function makeDownloadAction(
  label: string,
  tooltip: string,
  downloadParams: Parameters<typeof datatableExcelDownload>[0],
  fileName: string
): WidgetMoreAction {
  return {
    label,
    tooltip,
    cb: async () => {
      actionWithAsyncNotification(
        () => datatableExcelDownload(downloadParams),
        exportNotifications(fileName)
      );
    },
  };
}

export function widgetShareAction(
  widgetId: string,
  widgetTitle: string,
  action:
    | TableShareActionsConfigDto
    | TableauShareActionsConfigDto
    | VegaChartShareActionsConfigDto,
  t: TFunction,
  {
    pageFilterSelections,
    tableFilterSelections,
    workflowStateSelection,
    currentSortItems,
    customization,
    stakeAdjusted,
    groupByColumnId,
    onlyGroupRows,
  }: {
    pageFilterSelections?: ExcelExportRequestDto['selection'];
    tableFilterSelections?: ExcelExportRequestDto['selection'];
    workflowStateSelection?: ExcelExportRequestDto['workflowStateSelection'];
    currentSortItems?: SortColumnDto[];
    customization?: CustomizationDataDto;
    stakeAdjusted?: boolean;
    groupByColumnId?: string;
    onlyGroupRows?: boolean;
  } = {}
): WidgetMoreAction[] {
  const filteredExportFileName = portalFilenamify(
    `${dayjs().format(DOWNLOAD_DATE_PREFIX_FORMAT)} ${widgetTitle || `data-export-filtered-${widgetId}`}`
  );
  const unfilteredExportFileName = portalFilenamify(
    `${dayjs().format(DOWNLOAD_DATE_PREFIX_FORMAT)} ${widgetTitle || `data-export-${widgetId}`}`
  );
  const commonParams = {
    sortItems: currentSortItems,
    widgetId,
    customization,
    stakeAdjusted,
    groupByColumnId,
  };
  const commonFilteredParams = {
    ...commonParams,
    onlyGroupRows,
    selection: {
      ...pageFilterSelections,
      ...tableFilterSelections,
    },
    workflowStateSelection,
  };

  switch (action.actionKey) {
    case 'downloadXlsx':
      return [
        makeDownloadAction(
          t('app.widget.action.label.downloadXlsxFiltered'),
          t('app.widget.action.tooltip.downloadXlsxFiltered'),
          { ...commonFilteredParams, fileName: filteredExportFileName },
          filteredExportFileName
        ),
      ];
    case 'downloadCsv':
      return [
        makeDownloadAction(
          t('app.widget.action.label.downloadCsvFiltered'),
          t('app.widget.action.tooltip.downloadCsvFiltered'),
          {
            ...commonFilteredParams,
            fileName: filteredExportFileName,
            format: 'csv',
          },
          filteredExportFileName
        ),
      ];
    case 'downloadXlsxUnfiltered':
      return [
        makeDownloadAction(
          t('app.widget.action.label.downloadXlsxUnfiltered'),
          t('app.widget.action.tooltip.downloadXlsxUnfiltered'),
          { ...commonParams, fileName: unfilteredExportFileName },
          unfilteredExportFileName
        ),
      ];
    case 'downloadCsvUnfiltered':
      return [
        makeDownloadAction(
          t('app.widget.action.label.downloadCsvUnfiltered'),
          t('app.widget.action.tooltip.downloadCsvUnfiltered'),
          {
            ...commonParams,
            fileName: unfilteredExportFileName,
            format: 'csv',
          },
          unfilteredExportFileName
        ),
      ];
    case 'downloadXlsxReadOnly':
      return [
        makeDownloadAction(
          t('app.widget.action.label.downloadXlsxReadOnly'),
          t('app.widget.action.tooltip.downloadXlsxReadOnly'),
          {
            ...commonFilteredParams,
            fileName: unfilteredExportFileName,
            readOnly: true,
          },
          unfilteredExportFileName
        ),
      ];
    case 'copyContentId':
      return [
        {
          label: t('app.widget.action.label.copyContentId'),
          tooltip: t('app.widget.action.tooltip.copyContentId'),
          cb: async () => {
            actionWithAsyncNotification(
              async () => {
                if (navigator.clipboard?.writeText == null) {
                  throw new Error('Clipboard API is unavailable');
                }
                await navigator.clipboard.writeText(widgetId);
              },
              {
                successMessage: t('app.notifications.copyContentId.success'),
                errorMessage: (
                  <Trans
                    i18nKey="app.notifications.copyContentId.error"
                    values={{ id: widgetId }}
                  />
                ),
              }
            );
          },
        },
      ];
    default:
      return [
        {
          disabled: true,
          label: `${action.actionKey} not implemented`,
        },
      ];
  }
}

export function widgetGroupDataShareActions(
  widgetId: string,
  widgetTitle: string,
  t: TFunction,
  {
    canDownloadCsv,
    canDownloadXlsx,
    readOnlyXlsx,
    pageFilterSelections,
    tableFilterSelections,
    workflowStateSelection,
    currentSortItems,
    customization,
    stakeAdjusted,
    groupByColumnId,
  }: {
    canDownloadCsv: boolean;
    canDownloadXlsx: boolean;
    readOnlyXlsx?: boolean;
    pageFilterSelections?: ExcelExportRequestDto['selection'];
    tableFilterSelections?: ExcelExportRequestDto['selection'];
    workflowStateSelection?: ExcelExportRequestDto['workflowStateSelection'];
    currentSortItems?: SortColumnDto[];
    customization?: CustomizationDataDto;
    stakeAdjusted?: boolean;
    groupByColumnId?: string;
  }
): WidgetMoreAction[] {
  const groupExportFileName = portalFilenamify(
    `${dayjs().format(DOWNLOAD_DATE_PREFIX_FORMAT)} ${widgetTitle || `group-data-export-${widgetId}`}`
  );
  const commonParams = {
    sortItems: currentSortItems,
    widgetId,
    customization,
    stakeAdjusted,
    groupByColumnId,
    onlyGroupRows: true,
    skipAggregatedFilter: true,
    selection: {
      ...pageFilterSelections,
      ...tableFilterSelections,
    },
    workflowStateSelection,
  };
  const actions: WidgetMoreAction[] = [];

  if (canDownloadXlsx) {
    actions.push(
      makeDownloadAction(
        t('app.widget.action.label.downloadGroupDataXlsx'),
        t('app.widget.action.tooltip.downloadGroupDataXlsx'),
        {
          ...commonParams,
          fileName: groupExportFileName,
          ...(readOnlyXlsx && { readOnly: true }),
        },
        groupExportFileName
      )
    );
  }

  if (canDownloadCsv) {
    actions.push(
      makeDownloadAction(
        t('app.widget.action.label.downloadGroupDataCsv'),
        t('app.widget.action.tooltip.downloadGroupDataCsv'),
        { ...commonParams, fileName: groupExportFileName, format: 'csv' },
        groupExportFileName
      )
    );
  }

  return actions;
}

export function widgetSelectedRowsShareActions(
  widgetId: string,
  widgetTitle: string,
  t: TFunction,
  {
    actionRowIds,
    canDownloadCsv,
    canDownloadXlsx,
    currentSortItems,
    customization,
    readOnlyXlsx,
    stakeAdjusted,
    groupByColumnId,
  }: {
    actionRowIds: string[] | number[];
    canDownloadCsv: boolean;
    canDownloadXlsx: boolean;
    currentSortItems?: SortColumnDto[];
    customization?: CustomizationDataDto;
    readOnlyXlsx?: boolean;
    stakeAdjusted?: boolean;
    groupByColumnId?: string;
  }
): WidgetMoreAction[] {
  const selectedExportFileName = portalFilenamify(
    `${dayjs().format(DOWNLOAD_DATE_PREFIX_FORMAT)} ${widgetTitle || `data-export-selected-${widgetId}`}`
  );
  const baseParams = {
    actionRowIds,
    fileName: selectedExportFileName,
    sortItems: currentSortItems,
    widgetId,
    customization,
    stakeAdjusted,
    groupByColumnId,
  };
  const actions: WidgetMoreAction[] = [];

  if (canDownloadXlsx) {
    actions.push(
      makeDownloadAction(
        t('app.widget.action.label.downloadXlsxSelectedRows'),
        t('app.widget.action.tooltip.downloadXlsxSelectedRows'),
        { ...baseParams, ...(readOnlyXlsx && { readOnly: true }) },
        selectedExportFileName
      )
    );
  }

  if (canDownloadCsv) {
    actions.push(
      makeDownloadAction(
        t('app.widget.action.label.downloadCsvSelectedRows'),
        t('app.widget.action.tooltip.downloadCsvSelectedRows'),
        { ...baseParams, format: 'csv' },
        selectedExportFileName
      )
    );
  }

  return actions;
}
